'use client';

import { SessionProvider, useSession } from 'next-auth/react';
import { useMemo, type ReactNode } from 'react';
import type { AuthenticatedUser } from './types';

export { SessionProvider };

/**
 * Hook to get the current authenticated user from the next-auth session.
 * Returns a typed AuthenticatedUser object matching the backend interface.
 *
 * Must be used within a SessionProvider.
 */
export function useCurrentUser(): {
  user: AuthenticatedUser | null;
  accessToken: string | undefined;
  isLoading: boolean;
  isAuthenticated: boolean;
} {
  const { data: session, status } = useSession();

  const user = useMemo<AuthenticatedUser | null>(() => {
    if (!session?.user) return null;

    return {
      sub: (session.user as unknown as Record<string, unknown>).sub as string ?? '',
      email: session.user.email ?? undefined,
      name: session.user.name ?? undefined,
      customerNumber: session.user.customerNumber,
      roles: session.user.roles ?? [],
    };
  }, [session]);

  return {
    user,
    accessToken: session?.accessToken,
    isLoading: status === 'loading',
    isAuthenticated: status === 'authenticated',
  };
}
